<template>
  <div>
    <!-- Success Rates, Failure Rates & Top 10 Warehouses Widgets -->
    <b-row>
      <b-col
        cols="12"
      >
        <b-row>
          <!-- Success Rates -->
          <b-col
            cols="12"
            md="6"
          >
            <b-card class="h-90">
              <h5 class="metrics-heading">
                Most Success Branches
              </h5>
              <b-row class="justify-content-between metrics-row">
                <AppEchartPie
                  v-for="(branch, index) in successBranches"
                  :key="'success-' + index"
                  :percentage="branch.success_percentage"
                  :is-success="true"
                  :label="index"
                  :title="branch.name"
                  :start-color="'#228B22'"
                  :end-color="'#58e236'"
                />
              </b-row>
            </b-card>
          </b-col>

          <!-- Failure Rates -->
          <b-col
            cols="12"
            md="6"
          >
            <b-card class="h-90">
              <h5 class="metrics-heading">
                Most Failure Branches
              </h5>
              <b-row class="justify-content-between metrics-row">
                <AppEchartPie
                  v-for="(branch, index) in failureBranches"
                  :key="'failure-' + index"
                  :percentage="branch.failure_percentage"
                  :is-success="false"
                  :label="index"
                  :title="branch.name"
                  :start-color="'#d70000'"
                  :end-color="'#CD5C5C'"
                />
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <div>
      <b-card-actions
        title="Filters"
        action-collapse
        collapsed
      >
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Dispatch Date"
              label-for="dispatch-date"
            >
              <div class="position-relative">
                <AdvancedDateFilter
                  :date-value="filters['created_at']"
                  :opens="'right'"
                  :clear-icon="false"
                  @update="updateDateRange('created_at', $event)"
                />
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Apply and Clear Filters -->
        <b-row class="mt-1">
          <b-col>
            <filter-buttons
              :filter-data="filters"
              :apply-filters="applyFilters"
              :clear-filters="clearFilters"
            />
          </b-col>
        </b-row>
      </b-card-actions>
    </div>
    <div>
      <b-card no-body>
        <div class="m-2">
          <b-row>
            <b-col class="d-flex align-items-center justify-content-end mb-1 mb-md-0">
              <div>
                <ReportTableButtons
                  :items="items"
                  :json_fieldz="json_fields"
                  :name="`${downloadReportName} - Page ${page}.xls`"
                  :bulkname="`${downloadReportName}.xls`"
                  :fetch="fetchAssignOrderSummeryReportNoPagination"
                  @refresh="fetchAssignOrderSummeryReport"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <b-overlay
          id="overlay-background"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <div class="table-scroll-wrapper">
            <b-table
              striped
              hover
              responsive
              :per-page="perPage"
              :items="items"
              :fields="fields"
              :json_fields="json_fields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              show-empty
              class="assign-order-summary-table"
              @sort-changed="sortChanged"
            >
              <template #empty>
                <TableDataFetching
                  :rows="rows"
                  :data-loading="dataLoading"
                />
              </template>
              <template #head()="{ field }">
                <div
                  class="sortable-header"
                >
                  <span class="header-text">{{ field.label }}</span>
                  <feather-icon
                    v-if="field.sortable"
                    :icon="sort === field.key
                      ? 'ArrowUpIcon'
                      : 'ArrowDownIcon'"
                    size="14"
                  />
                </div>
              </template>
              <template #head(current_invalid_destination)="{ field }">
                <div class="text-left d-flex align-items-center">

                  <div class="sortable-header">
                    <span class="header-text">Current DD</span>
                    <feather-icon
                      v-if="field.sortable"
                      :icon="sort === field.key
                        ? 'ArrowUpIcon'
                        : 'ArrowDownIcon'"
                      size="14"
                    />
                    <feather-icon
                      v-b-tooltip.hover.html="'<div style=&quot;text-align: left; white-space: normal;&quot;>All DD order counts until 6 am of the end date</div>'"
                      icon="InfoIcon"
                      size="16"
                      class="ml-1"
                    />
                  </div>
                </div>
              </template>
              <template #cell(delivery_percentage)="data">
                <b-badge :variant="getDeliveryPercentageVariant(data.value)">
                  {{ data.value }}%
                </b-badge>
              </template>

              <!-- Keep #custom-foot here -->
              <template #custom-foot>
                <b-tr class="bg-light total-row-footer">
                  <b-th class="text-left">
                    Total:
                  </b-th>
                  <b-th class="text-right">
                    {{ totalPendingYesterday }}
                  </b-th>
                  <b-th class="text-right">
                    {{ totalYesterdayDeliveries }}
                  </b-th>
                  <b-th class="text-right">
                    {{ totalFailedYesterday }}
                  </b-th>
                  <b-th class="text-right">
                    {{ totalCurrentInvalidDestination }}
                  </b-th>
                  <b-th class="text-right">
                    {{ totalDispatchedToday }}
                  </b-th>
                  <b-th class="text-right">
                    {{ totalTotalPending }}
                  </b-th>
                  <b-th class="text-right">
                    <!-- No total for percentage column, or you can calculate an average if desired -->
                  </b-th>
                </b-tr>
              </template>

            </b-table>
          </div>
        </b-overlay>
        <div class="mx-2 my-2">
          <b-row>
            <b-col
              cols="12"
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label>Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-1 width-125"
              />
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span class="text-muted pagination-text  mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{
                meta.to }}
                of {{ meta.total }} entries</span>
              <b-pagination
                v-model="page"
                :total-rows="(meta.total) ? meta.total : 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BRow,
  BTable,
  BBadge,
  VBModal,
  BOverlay,
  BPagination,
  BTr,
  BTh,
  VBTooltip,
} from 'bootstrap-vue'
import AppEchartPie from '@core/components/charts/echart/AppEchartPie.vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import FilterButtons from '@/components/Filter/ReportFilterButtons.vue'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'

const ReportRepository = RepositoryFactory.get('report')

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
  },
  components: {
    BCardActions,
    vSelect,
    BCard,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BOverlay,
    BPagination,
    BBadge,
    BTr,
    BTh,
    FilterButtons,
    AppEchartPie,
    AdvancedDateFilter,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage],
  data() {
    return {
      filters: {
        created_at: {
          startDate: null,
          endDate: null,
        },
      },
      dataLoading: false,
      sort: '',
      perPage: 100,
      pageOptions: [100, 200, 500],
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      fields: [
        {
          key: 'warehouse_name',
          label: 'Branch Name',
          thClass: 'text-left',
          tdClass: 'text-left',
          thStyle: { width: '20%' },
        },
        {
          key: 'pending_yesterday',
          label: 'Old Pendings',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
          thStyle: { width: '10%' },
        },
        {
          key: 'yesterday_deliveries',
          label: 'Yesterday Deliveries',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
          thStyle: { width: '15%' },
        },
        {
          key: 'failed_yesterday',
          label: 'Yesterday Fail to Deliver',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
          thStyle: { width: '15%' },
        },
        {
          key: 'current_invalid_destination',
          label: 'Current DD',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
          thStyle: { width: '10%' },
        },
        {
          key: 'dispatched_today',
          label: 'Today Dispatched',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
          thStyle: { width: '15%' },
        },
        {
          key: 'total_pending',
          label: 'Total Pending',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
          thStyle: { width: '10%' },
        },
        {
          key: 'delivery_percentage',
          label: 'Delivery %',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
          thStyle: { width: '10%' },
        },
      ],
      json_fields: {
        'Branch Name': {
          field: 'warehouse_name',
          callback: value => `"${value}"`,
        },
        'Old Pendings': 'pending_yesterday',
        'Yesterday Deliveries': 'yesterday_deliveries',
        'Yesterday Fail to Deliver': 'failed_yesterday',
        'Current DD': 'current_invalid_destination',
        'Today Dispatched': 'dispatched_today',
        'Total Pending': 'total_pending',
        'Delivery %': 'delivery_percentage',
      },
      downloadReportName: 'Assign Order Summery Report',
      items: [],
      successBranches: [],
      failureBranches: [],
    }
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
    totalPendingYesterday() {
      return this.items.reduce((acc, item) => acc + (parseFloat(item.pending_yesterday) || 0), 0)
    },
    totalYesterdayDeliveries() {
      return this.items.reduce((acc, item) => acc + (parseFloat(item.yesterday_deliveries) || 0), 0)
    },
    totalFailedYesterday() {
      return this.items.reduce((acc, item) => acc + (parseFloat(item.failed_yesterday) || 0), 0)
    },
    totalCurrentInvalidDestination() {
      return this.items.reduce((acc, item) => acc + (parseFloat(item.current_invalid_destination) || 0), 0)
    },
    totalDispatchedToday() {
      return this.items.reduce((acc, item) => acc + (parseFloat(item.dispatched_today) || 0), 0)
    },
    totalTotalPending() {
      return this.items.reduce((acc, item) => acc + (parseFloat(item.total_pending) || 0), 0)
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchAssignOrderSummeryReport()
      },
    },
    deep: true,
    page() {
      this.fetchAssignOrderSummeryReport()
    },
    perPage() {
      this.fetchAssignOrderSummeryReport()
    },
  },
  mounted() {
    this.setDefaultFilter()
    this.fetchAssignOrderSummeryReport()
    this.getWarehouseList()
  },
  methods: {
    async fetchAssignOrderSummeryReport() {
      this.dataLoading = true
      this.items = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await ReportRepository.getAssignOrderSummeryReport(this.page, filterQuery, this.perPage))
        // 1. Calculate percentages for the table
        const processedItems = data.data.map(item => {
          const todayDispatched = parseFloat(item.dispatched_today) || 0
          const yesterdayFailed = parseFloat(item.failed_yesterday) || 0
          const oldPending = parseFloat(item.pending_yesterday) || 0
          const yesterdayDd = parseFloat(item.current_invalid_destination) || 0
          const yesterdayDelivered = parseFloat(item.delivered_yesterday) || 0
          const yesterdayPartial = parseFloat(item.partial_delivered_yesterday) || 0

          const totalYesterdayDeliveries = yesterdayDelivered + yesterdayPartial
          const totalPending = (oldPending - yesterdayPartial - yesterdayDelivered - yesterdayFailed - yesterdayDd) + todayDispatched

          const rawDeliveryPerc = totalYesterdayDeliveries > 0 ? (totalYesterdayDeliveries / oldPending) * 100 : 0
          const rawReturnPerc = todayDispatched > 0 ? (yesterdayFailed / todayDispatched) * 100 : 0

          return {
            ...item,
            yesterday_deliveries: totalYesterdayDeliveries,
            total_pending: totalPending,
            delivery_percentage: Math.min(rawDeliveryPerc, 100).toFixed(2),
            return_percentage: Math.min(rawReturnPerc, 100).toFixed(2),
          }
        })

        this.items = processedItems.sort((a, b) => parseFloat(b.delivery_percentage) - parseFloat(a.delivery_percentage))
        this.meta = data.meta

        this.successBranches = [...processedItems]
          .sort((a, b) => parseFloat(b.delivery_percentage) - parseFloat(a.delivery_percentage))
          .slice(0, 4)
          .map(branch => ({
            name: branch.warehouse_name,
            success_percentage: parseFloat(branch.delivery_percentage),
          }))

        this.failureBranches = [...processedItems]
          .sort((a, b) => parseFloat(b.return_percentage) - parseFloat(a.return_percentage))
          .slice(0, 4)
          .map(branch => ({
            name: branch.warehouse_name,
            failure_percentage: parseFloat(branch.return_percentage),
          }))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchAssignOrderSummeryReportNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await ReportRepository.getAssignOrderSummeryReportNoPagination(filterQuery))
        // 1. Process and calculate fields
        const processedData = data.data.map(item => {
          const oldPending = parseFloat(item.pending_yesterday) || 0
          const yesterdayFailed = parseFloat(item.failed_yesterday) || 0
          const yesterdayDd = parseFloat(item.current_invalid_destination) || 0
          const todayDispatched = parseFloat(item.dispatched_today) || 0
          const yesterdayDelivered = parseFloat(item.delivered_yesterday) || 0
          const yesterdayPartial = parseFloat(item.partial_delivered_yesterday) || 0
          const totalYesterdayDeliveries = yesterdayDelivered + yesterdayPartial

          const rawDeliveryPerc = totalYesterdayDeliveries > 0 ? (totalYesterdayDeliveries / oldPending) * 100 : 0

          return {
            ...item,
            yesterday_deliveries: totalYesterdayDeliveries,
            total_pending: (oldPending - yesterdayPartial - yesterdayDelivered - yesterdayFailed - yesterdayDd) + todayDispatched,
            delivery_percentage: Math.min(rawDeliveryPerc, 100).toFixed(2),
          }
        })

        // 2. Sort the data based on delivery_percentage in descending order
        // We use parseFloat because toFixed(2) turns the value into a string
        return processedData.sort((a, b) => parseFloat(b.delivery_percentage) - parseFloat(a.delivery_percentage))
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    getDeliveryPercentageVariant(value) {
      const percentage = parseFloat(value) || 0

      if (percentage <= 25) {
        return 'danger'
      }
      if (percentage <= 50) {
        return 'warning'
      }
      if (percentage <= 75) {
        return 'info'
      }
      return 'success'
    },
    handleChangePage(page) {
      this.page = page
      this.fetchAssignOrderSummeryReport()
    },
    setDefaultFilter() {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
      const currentDate = {
        startDate: yesterday.setHours(6, 0, 0, 0),
        endDate: today.setHours(6, 0, 0, 0),
      }
      this.updateDateRange('created_at', currentDate)
    },
    applyFilters() {
      this.fetchAssignOrderSummeryReport()
    },
    clearFilters() {
      this.filters = {
        created_at: {
          startDate: null,
          endDate: null,
        },
      }
      this.setDefaultFilter()
      this.$nextTick(() => {
        this.fetchAssignOrderSummeryReport()
      })
    },
    getColumnWidth(key) {
      const field = this.fields.find(f => f.key === key)
      return field && field.thStyle && field.thStyle.width ? field.thStyle.width : 'auto'
    },
    sortChanged(e) {
      const key = e.sortBy
      if (key) {
        const active = e.sortDesc ? '-' : ''
        this.sort = active + key
      }
    },
  },
}
</script>
<style lang="scss">
@import '../../../@core/scss/vue/libs/vue-select';

.warehouse-heading {
    font-weight: bold;
    color: #000;
}

.warehouse-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.warehouse-list li {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 6px;
    border-bottom: 1px solid #e0e0e0;
    font-size: 12px;
}

.warehouse-list li:last-child {
    border-bottom: none;
}

.rank {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-right: 5px;
    font-weight: bold;
    color: #333;
    border-radius: 50%;
    background: #f1f1f1;
    text-align: center;
}

.top-rank {
    background: #58e236;
    color: #fff;
}

.name {
    font-weight: 500;
    color: #333;
}

.metrics-heading {
    color: #000000;
    font-weight: bold;
}

.metrics-row {
    border-top: 1px solid #D9D9D9;
}

.h-90 {
    height: 90% !important;
}

.no-data-row {
    min-height: 150px;
}

.no-data-text {
    color: #000 !important;
}

.assign-order-summary-table {
  /* No sticky-header here, we're managing scrolling with a wrapper */
  margin-bottom: 0 !important; /* Remove default table bottom margin */
}

/* Wrapper to control scrolling */
.table-scroll-wrapper {
  max-height: 500px; /* Adjust this height as needed */
  overflow-y: auto; /* Enable vertical scrolling */
  position: relative; /* Crucial for sticky children */
}

/* Apply sticky to tfoot */
.table-scroll-wrapper .b-table tfoot {
  position: sticky;
  bottom: 0;
  z-index: 2; /* Ensure it stays above tbody content */
  background-color: #f8f8f8; /* Match your card background or choose a suitable one */
  border-top: 1px solid #e0e0e0; /* Optional: border to separate from body */
}

/* Apply sticky to thead (optional, but good for consistency) */
.table-scroll-wrapper .b-table thead {
  position: sticky;
  top: 0;
  z-index: 2; /* Ensure it stays above tbody content */
  background-color: #f8f8f8; /* Match the header's original background */
  border-bottom: 1px solid #e0e0e0;
}

/* Fix for the width of sticky headers/footers in responsive tables */
.table-scroll-wrapper .table-responsive {
  overflow: visible; /* Prevent this from interfering with sticky */
}

.table-scroll-wrapper .b-table table {
  table-layout: fixed; /* Helps with consistent column widths when scrolling */
  width: 100%; /* Ensure table takes full width of wrapper */
}

.assign-order-summary-table tfoot th {
  font-size: 1rem;
}

@media (min-width: 767px) {
    .status-dropdown .vs__dropdown-menu {
        width: fit-content;
    }
}
</style>
